<template>
  <div id="app" :class="shellClasses">
    <electron-menu></electron-menu>
    <router-view></router-view>
    <div v-if="showBootstrapShell" class="app-bootstrap-shell">
      <div class="app-bootstrap-shell__loader" aria-hidden="true">
        <div class="app-bootstrap-shell__ring"></div>
        <div class="app-bootstrap-shell__ring app-bootstrap-shell__ring--inner"></div>
        <div class="app-bootstrap-shell__orbit"></div>
        <div class="app-bootstrap-shell__core"></div>
      </div>
      <div class="app-bootstrap-shell__footer">
        <div class="app-bootstrap-shell__title">{{ bootstrapTitle }}</div>
        <div class="app-bootstrap-shell__hint">{{ bootstrapHint }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import ElectronMenu from './components/common/ElectronMenu.vue';

export default {
  name: 'App',
  components: { ElectronMenu },
  data() {
    return {
      bootstrapSettled: false,
      bootstrapRoutePaintReady: false,
      bootstrapReadyReported: false,
      bootstrapResolveToken: 0,
      bootstrapResolveFrame: 0,
      bootstrapResolveFallbackTimer: null
    };
  },
  computed: {
    isAuthRoute() {
      const authRouteNames = ['TenantEntry', 'Login', 'Register', 'ResetPwd'];
      return authRouteNames.includes(this.$route && this.$route.name);
    },
    workspaceReady() {
      return Boolean(this.configStore && this.configStore.workspaceReady);
    },
    connectionHint() {
      return this.configStore && this.configStore.connection
        ? String(this.configStore.connection.hint || '').trim()
        : '';
    },
    showBootstrapShell() {
      return !this.bootstrapSettled;
    },
    bootstrapTitle() {
      const routeName = this.$route && this.$route.name;
      if (routeName === 'TenantEntry') {
        return '正在连接租户通信入口';
      }
      if (routeName === 'Login') {
        return '正在恢复登录状态';
      }
      if (routeName === 'Register' || routeName === 'ResetPwd') {
        return '正在准备安全验证';
      }
      return '正在连接消息服务';
    },
    bootstrapHint() {
      const routeName = this.$route && this.$route.name;
      if (routeName === 'TenantEntry') {
        return '正在校验租户配置与通信线路。';
      }
      if (routeName === 'Login') {
        return '正在准备最近账号、租户与安全配置。';
      }
      if (this.isAuthRoute) {
        return '正在加载即时通信认证与身份校验资源。';
      }
      return this.connectionHint || '正在同步最近会话、联系人与未读消息。';
    },
    shellClasses() {
      const isElectron = Boolean(this.configStore && this.configStore.electronMode);
      const isDesktop = this.configStore ? this.configStore.isDesktop : true;
      return {
        'app-shell--electron': isElectron,
        'app-shell--auth': this.isAuthRoute,
        'app-shell--workspace': !this.isAuthRoute,
        'app-shell--desktop': isDesktop,
        'app-shell--mobile': !isDesktop
      };
    }
  },
  watch: {
    '$route.fullPath'() {
      this.queueBootstrapResolve();
    },
    workspaceReady() {
      this.resolveBootstrapShell();
    }
  },
  mounted() {
    this.queueBootstrapResolve();
  },
  beforeDestroy() {
    this.invalidateBootstrapResolve();
  },
  methods: {
    invalidateBootstrapResolve() {
      this.bootstrapResolveToken += 1;
      if (this.bootstrapResolveFrame && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(this.bootstrapResolveFrame);
      }
      this.bootstrapResolveFrame = 0;
      if (this.bootstrapResolveFallbackTimer) {
        clearTimeout(this.bootstrapResolveFallbackTimer);
        this.bootstrapResolveFallbackTimer = null;
      }
    },
    queueBootstrapResolve() {
      if (this.bootstrapSettled) {
        return;
      }
      this.invalidateBootstrapResolve();
      const resolveToken = this.bootstrapResolveToken;
      this.bootstrapRoutePaintReady = false;
      this.$nextTick(() => {
        const markPaintReady = () => {
          if (this.bootstrapSettled || resolveToken !== this.bootstrapResolveToken || this.bootstrapRoutePaintReady) {
            return;
          }
          if (this.bootstrapResolveFallbackTimer) {
            clearTimeout(this.bootstrapResolveFallbackTimer);
            this.bootstrapResolveFallbackTimer = null;
          }
          this.bootstrapResolveFrame = 0;
          this.bootstrapRoutePaintReady = true;
          this.reportBootstrapReady();
          this.resolveBootstrapShell();
        };
        const fallbackDelay = this.configStore && this.configStore.electronMode ? 160 : 260;
        // Electron 窗口隐藏阶段 requestAnimationFrame 可能被节流，导致主进程迟迟收不到
        // bootstrap-shell:ready。这里保留双帧等待，同时加一个短超时兜底确保启动壳可及时显示。
        this.bootstrapResolveFallbackTimer = setTimeout(() => {
          this.bootstrapResolveFallbackTimer = null;
          markPaintReady();
        }, fallbackDelay);
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          markPaintReady();
          return;
        }
        this.bootstrapResolveFrame = window.requestAnimationFrame(() => {
          this.bootstrapResolveFrame = window.requestAnimationFrame(() => {
            markPaintReady();
          });
        });
      });
    },
    reportBootstrapReady() {
      if (this.bootstrapReadyReported) {
        return;
      }
      if (typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.sendEvent === 'function') {
        window.electronAPI.sendEvent('bootstrap-shell:ready');
      }
      this.bootstrapReadyReported = true;
    },
    resolveBootstrapShell() {
      if (this.bootstrapSettled || !this.bootstrapRoutePaintReady) {
        return;
      }
      if (this.isAuthRoute || this.workspaceReady) {
        this.bootstrapSettled = true;
      }
    }
  }
}
</script>

<style lang="scss">
#app {
  --electron-chrome-height: 0px;
  --electron-shell-bg: var(--im-background);
  --electron-shell-border: var(--im-border-color);
  --electron-shell-text: var(--im-text-color);
  --electron-shell-hover-bg: var(--im-icon-hover-bg);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: absolute;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  color: var(--im-text-color);
  font-family: var(--im-font-family);
  background: var(--electron-shell-bg);
  transition: background 0.2s ease, color 0.2s ease;
}

.app-bootstrap-shell {
  position: absolute;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--im-background);
}

.app-bootstrap-shell__loader {
  position: relative;
  width: 68px;
  height: 68px;
}

.app-bootstrap-shell__ring,
.app-bootstrap-shell__orbit,
.app-bootstrap-shell__core {
  position: absolute;
  inset: 0;
  border-radius: 999px;
}

.app-bootstrap-shell__ring {
  box-sizing: border-box;
  border: 3px solid rgba(var(--im-primary-rgb), 0.14);
  border-top-color: var(--im-color-primary);
  border-right-color: rgba(var(--im-primary-rgb), 0.42);
  animation: bootstrap-shell-spin 1s linear infinite;
}

.app-bootstrap-shell__ring--inner {
  inset: 10px;
  border-width: 2px;
  border-color: rgba(var(--im-primary-rgb), 0.1);
  border-bottom-color: rgba(var(--im-primary-rgb), 0.65);
  border-left-color: rgba(var(--im-primary-rgb), 0.32);
  animation-direction: reverse;
  animation-duration: 1.3s;
}

.app-bootstrap-shell__orbit {
  animation: bootstrap-shell-spin 1.6s linear infinite;
}

.app-bootstrap-shell__orbit::before,
.app-bootstrap-shell__orbit::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 999px;
  background: var(--im-color-primary);
}

.app-bootstrap-shell__orbit::before {
  width: 8px;
  height: 8px;
  margin-top: -4px;
  margin-left: -4px;
  transform: translateY(-30px);
}

.app-bootstrap-shell__orbit::after {
  width: 6px;
  height: 6px;
  margin-top: -3px;
  margin-left: -3px;
  background: rgba(var(--im-primary-rgb), 0.42);
  transform: rotate(180deg) translateY(-30px);
}

.app-bootstrap-shell__core {
  inset: 24px;
  background: rgba(var(--im-primary-rgb), 0.12);
  animation: bootstrap-shell-pulse 1.2s ease-in-out infinite;
}

.app-bootstrap-shell__footer {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}

.app-bootstrap-shell__title {
  font-size: 15px;
  line-height: 1.4;
  font-weight: 600;
  color: var(--im-text-color);
}

.app-bootstrap-shell__hint {
  max-width: 440px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--im-text-color-light);
}

@keyframes bootstrap-shell-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes bootstrap-shell-pulse {
  0%, 100% {
    transform: scale(0.85);
    opacity: 0.45;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-bootstrap-shell__ring,
  .app-bootstrap-shell__orbit,
  .app-bootstrap-shell__core {
    animation: none;
  }
}

#app.app-shell--electron {
  --electron-chrome-height: 25px;
  padding-top: var(--electron-chrome-height);
}

#app.app-shell--electron.app-shell--auth {
  --electron-shell-bg: var(--im-background);
  --electron-shell-border: var(--im-border-color);
  --electron-shell-text: var(--im-text-color);
  --electron-shell-hover-bg: var(--im-icon-hover-bg);
}

#app.app-shell--electron.app-shell--workspace.app-shell--desktop {
  --electron-shell-bg: linear-gradient(
    90deg,
    var(--im-navi-bg) 0,
    var(--im-navi-bg) 78px,
    var(--im-background) 78px,
    var(--im-background) 100%
  );
  --electron-shell-border: var(--im-surface-panel-border);
  --electron-shell-text: var(--im-icon-color-strong);
  --electron-shell-hover-bg: rgba(var(--im-primary-rgb), 0.12);
}

#app.app-shell--electron.app-shell--workspace.app-shell--mobile {
  --electron-shell-bg: var(--im-chat-bg);
  --electron-shell-border: var(--im-border-color);
  --electron-shell-text: var(--im-text-color-primary);
  --electron-shell-hover-bg: var(--im-background-active);
}
</style>
